import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useManualQuery } from "graphql-hooks";
import {
  Avatar,
  Box,
  Button,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PublicPageLayout from "./PublicPageLayout";
import ProductCard from "../components/ProductCard";
import FloatingMenu from "../components/FloatingMenu";
import AlertPopup from "../components/AlertPopup";
import EmptyState from "../components/EmptyState";
import dayjs from "dayjs";
import { isValidUUIDv4 } from "../hooks/uuid";

import useIsTablet from "../hooks/useIsTablet";
import useIsMobile from "../hooks/useIsMobile";

import { STORIES_BY_USER_UUID } from "../graphQL/story_qry";
import {
  CREATE_COLLECTION_MUTATION,
  DELETE_COLLECTION_MUTATION,
} from "../graphQL/collection_qry";

const sortList = ["Ascending", "Descending"];

//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

const PublicStories = () => {
  const navigate = useNavigate();
  const { userUuid } = useParams();
  const [stories, setStories] = useState([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("Ascending");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isFollowing, setIsFollowing] = useState(false);
  const [floatMenuLeftMargin, setFloatMenuLeftMargin] = useState(0);
  const [alertPopup, setAlertPopup] = useState({
    open: false,
    type: "",
    productId: null,
    productTitle: "",
  });
  const [warningPopup, setWarningPopup] = useState({
    open: false,
  });
  const [isLoaded, setIsLoaded] = useState(false);

  //remove from collection by sami
  const [confirmPopup, setConfirmPopup] = useState({
    open: false,
    productId: 0,
    title: "",
  });

  //add to collection by sami
  const [successPopup, setSuccessPopup] = useState({
    open: false,
    productId: 0,
    title: "",
  });

  //Sami
  const [createCollection] = useMutation(CREATE_COLLECTION_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [deleteCollection] = useMutation(DELETE_COLLECTION_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [getStoriesData] = useManualQuery(STORIES_BY_USER_UUID, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    setStories(prev =>
      prev.map(story => ({
        ...story,
        userId: {
          ...story.userId,
          isFollowing,
        },
      }))
    );
  }, [isFollowing]);

  const setStoryList = async () => {
    if (!userUuid) return;
    if (isValidUUIDv4(userUuid)) {
      const {
        loading: loadingStoryListData,
        error: errorStoryListData,
        data: storyListData,
      } = await getStoriesData({
        variables: {
          userUuid: userUuid,
          status: "Published",
        },
      });
      if (
        !loadingStoryListData &&
        storyListData &&
        storyListData.storiesByUseruuid
      ) {
        const firstStoryUser =
         storyListData.storiesByUseruuid[0]?.userId;

        if (firstStoryUser) {
          setIsFollowing(firstStoryUser.isLoggedInUserFollowing);
        }
        setStories(
          storyListData.storiesByUseruuid.map((story) => ({
            id: story.storyId,
            userId: {
              userId: story.userId.id,
              userUuid: story.userId.userUuid,
              firstName: story.userId.firstName,
              lastName: story.userId.lastName,
              username: story.userId.username,
              avatarFileName: story.userId.avatarFileName,
              isFollowing: story.userId.isLoggedInUserFollowing,
              listings: story.userId.listings,
              soldToys: story.userId.soldToys,
              ratingNo: story.userId.ratingNo,
              ratingCount: story.userId.ratingCount,
              dateJoined: story.userId.dateJoined,
            },
            uuid: story.uuid,
            img: story.fileName,
            title: story.title,
            content: story.content,
            contentType: "Stories",
            isLiked: story.isLiked,
            shareUrl: `${window.location.origin}/story/${story.uuid}`,
            createdOn: dayjs(story.createDate).format("MMM D, YYYY"),
            publishedOn: story.isPublished
              ? dayjs(story.publishedDate).format("MMM D, YYYY")
              : "",
            status: story.isPublished ? "Published" : "Draft",
            //photoName: story.fileName?.split("/").pop(),
            //photoSize: story.fileSize ? (story.fileSize/ 1024).toFixed(0): ""
            likedUsers: story.storyLikes.map((storyLike, index) => {
              return {
                id: storyLike.storyLikeId,
                username: storyLike.userId.username,
                avatar: storyLike.userId.avatarFileName,
                isFollowing: storyLike.userId.isLoggedInUserFollowing,
              };
            }),
            likes: story.likeNo,
            shares: story.shareNo,
            isFavorited: false,
            isInCollection: story.isInCollection,
            commentNo: story.commentNo,
            comments: story.storyComments,
          }))
        );
        setIsLoaded(true);
      } else {
        console.log("errorCurrStoryData: ", errorStoryListData);
      }
    } else {
      setWarningPopup({
        open: true,
        title: "This URL is not valid.",
      });
    }
  };

  useEffect(() => {
    (async () => setStoryList())();
  }, [userUuid]);

  const isTablet = useIsTablet(1360); // Use 1360px breakpoint for tablet size
  const isMobile = useIsMobile(856); // Use 856px breakpoint for mobile size

  let loggedUserName = localStorage.getItem("userName")
    ? localStorage.getItem("userName")
    : null;

  // Set rows per page based on screen size
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 833;
      setRowsPerPage(isMobile ? 5 : 10);
      setPage(0); // Reset to first page when changing rows per page
      setFloatMenuLeftMargin(window.innerWidth - 100);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Pagination handlers
  //const handleChangePage = (event, newPage) => {
  //setPage(newPage);
  //};

  //const handleChangeRowsPerPage = (event) => {
  //setRowsPerPage(parseInt(event.target.value));
  //setPage(0);
  //};

  // Filter and sort stories
  const getFilteredAndSortedStories = () => {
    let filtered = stories.filter((s) =>
      s.title.toLowerCase().includes(search.toLowerCase())
    );

    // Sort by title
    filtered.sort((a, b) => {
      const titleA = a.title.toLowerCase();
      const titleB = b.title.toLowerCase();

      if (sortBy === "Ascending") {
        return titleA.localeCompare(titleB);
      } else {
        return titleB.localeCompare(titleA);
      }
    });

    return filtered;
  };

  const filteredStories = getFilteredAndSortedStories();

  // Get current page data from filtered and sorted results
  const paginatedStories = filteredStories.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Handle bookmark click to show confirmation popup
  const handleBookmarkClick = (productId, isInCollection) => {
    const product = stories.find((p) => p.id === productId);
    if (isInCollection) {
      // Show removal confirmation
      setConfirmPopup({
        open: true,
        productId: productId,
        title: product ? product.title : "",
      });
    } else {
      // Show add alert
      setSuccessPopup({
        open: true,
        productId: productId,
        title: product ? product.title : "",
      });
    }
  };

  const handleConfirmAdd = async () => {
    await createCollection({
      variables: {
        objectId: parseInt(successPopup.productId),
        contentType: "story",
      },
    });
    setSuccessPopup({ open: false, productId: 0, title: "" });

    setStories((prev) =>
      prev.map((product) =>
        product.id === successPopup.productId
          ? { ...product, isInCollection: !product.isInCollection }
          : product
      )
    );
  };

  const handleConfirmRemove = async () => {
    await deleteCollection({
      variables: {
        objectId: parseInt(confirmPopup.productId),
        contentType: "story",
      },
    });

    setStories((prev) =>
      prev.map((product) =>
        product.id === confirmPopup.productId
          ? { ...product, isInCollection: !product.isInCollection }
          : product
      )
    );
    setConfirmPopup({ open: false, productId: 0, title: "" });
  };

  // Handle cancel action
  const handleCancelAction = () => {
    setConfirmPopup({ open: false, productId: 0, title: "" });
    setSuccessPopup({ open: false, productId: 0, title: "" });
  };

  const handleUserProfileNavigation = (user) => {
    // Navigate to user's public homepage
    navigate(`/user/${user.userUuid}/profile`);
  };

  const handleUserFollowUpdate = (newIsFollowing) => {
    setIsFollowing(newIsFollowing);

    setStories(prev =>
      prev.map(story => ({
        ...story,
        userId: {
          ...story.userId,
          isFollowing: newIsFollowing,
        },
      }))
    );
  };
  // Handle share click to increment share count
  const handleShareClick = (productId) => {
    setStories((prev) =>
      prev.map((product) =>
        product.id === productId
          ? { ...product, shares: (product.shares || 0) + 1 }
          : product
      )
    );
  };

  return (
    <PublicPageLayout
      activeMenuItem="stories"
      userUuid={userUuid}
      isFollowing={isFollowing}
      onFollowChange={setIsFollowing}
    >
      {/* Header */}
      <Box p={2}>
        <Typography variant="h6">Stories</Typography>
        <Typography variant="body2" color="text.secondary">
          Where every toy sparks a new adventure.
        </Typography>
      </Box>

      {/* Divider under header */}
      <Divider sx={{ mb: 2 }} />

      {/* Search and Sort Section */}
      <Box pr={2} pl={2}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            bgcolor: "#F5F5F5", // Gray box around
            p: "16px",
            borderRadius: 2,
            "@media (max-width: 833px)": {
              flexDirection: "column",
              alignItems: "stretch",
            },
          }}
        >
          {/* Search box */}
          <TextField
            fullWidth
            placeholder="Search stories..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              bgcolor: "#fff",
              flexGrow: 1,
              borderRadius: 1,
              "& .MuiOutlinedInput-root": {
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d0d0d0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                  borderWidth: "1px",
                },
              },
            }}
          />

          {/* Sort dropdown */}
          <FormControl
            size="small"
            sx={{
              minWidth: "200px",
              "@media (max-width: 833px)": {
                width: "100%",
                mt: 1, // Add margin top on mobile
              },
              bgcolor: "#fff",
              borderRadius: 1,
            }}
          >
            <InputLabel
              sx={{
                color: "#707070",
                "&.Mui-focused": { color: "#707070" },
              }}
            >
              Sort by
            </InputLabel>
            <Select
              label="Sort by"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              displayEmpty
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d0d0d0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d0d0d0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d0d0d0",
                  borderWidth: "1px",
                },
              }}
            >
              {sortList.map((srt) => (
                <MenuItem key={srt} value={srt}>
                  {srt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Divider under search section */}
      <Divider />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent:
            isLoaded && filteredStories.length === 0 ? "center" : "flex-start",
          gap: "24px",
          width: "912px",
          m: 2,
          pt: "1px",
          "@media (max-width: 1360px)": {
            width: "626px",
            gap: "16px",
            justifyContent: "center",
            mb: 2,
            mx: "auto",
          },
          "@media (max-width: 856px)": {
            width: "361px",
            justifyContent: "center",
            gap: 0,
            mx: "auto",
          },
        }}
      >
        {!isLoaded ? (
          <Box p={4} textAlign="center">
            Loading...
          </Box>
        ) : isLoaded && stories.length === 0 ? (
          <EmptyState
            type="stories"
            title="No Stories Available"
            description="No stories are available right now. Please check back later."
          />
        ) : (
          <>
            {filteredStories.map((p, index) => {
              return (
                <Box
                  key={p.id}
                  sx={{
                    width: "288px",
                    position: "relative",
                    "@media (max-width: 856px)": {
                      mb: "24px",
                    },
                  }}
                >
                  <ProductCard
                    p={p}
                    onBookmarkClick={handleBookmarkClick}
                    onUserProfileNavigation={handleUserProfileNavigation}
                    onFollowToggle={handleUserFollowUpdate}
                    onShareClick={handleShareClick}
                  />
                </Box>
              );
            })}
          </>
        )}
      </Box>

      {/* Alert Popup for add collection actions */}
      <AlertPopup
        open={successPopup.open}
        title={`Item "${successPopup.title}" is added to your collection.`}
        cancelText=""
        confirmText="OK"
        onConfirm={handleConfirmAdd}
        onClose={() =>
          setSuccessPopup({ open: false, productId: 0, title: "" })
        }
      />

      {/* Alert Popup for remove collection actions */}
      <AlertPopup
        open={confirmPopup.open}
        title={`Are you sure you want to remove "${confirmPopup.title}" from your collection?`}
        confirmText="Yes, Remove"
        cancelText="Cancel"
        onConfirm={handleConfirmRemove}
        onCancel={() =>
          setConfirmPopup({ open: false, productId: 0, title: "" })
        }
        onClose={() =>
          setConfirmPopup({ open: false, productId: 0, title: "" })
        }
      />

      <AlertPopup
        open={warningPopup.open}
        title={warningPopup.title}
        message=""
        confirmText="OK"
        cancelText=""
        onConfirm={() => setWarningPopup(false)}
        onClose={() => setWarningPopup(false)}
      />

      <FloatingMenu left={floatMenuLeftMargin} />
    </PublicPageLayout>
  );
};

export default PublicStories;
